/**
 * Edit a campaign that is on Meta, from the create canvas.
 *
 * GET /api/ads/campaigns/:id, read live from Meta, carries the first ad set
 * (targeting, budget, dates) and the first ad's creative; draftFromCampaign
 * fills the canvas from them and the filled draft is kept as the base. Saving
 * sends only what differs from the base, one part at a time, so each part
 * succeeds or fails on its own:
 *   name ............................. PATCH /api/ads/campaigns/:id { name }
 *   budget, schedule, who sees it .... PATCH /api/ads/adsets/:id { daily_budget, start_time, end_time, targeting }
 *   words, link, button, picture ..... PATCH /api/ads/ads/:id/creative (a new creative; Meta reviews the ad again)
 *   Olli optimising .................. PUT /api/ads/campaigns/:id/ai-optimization
 * A new picture goes as image_url and a new video as video_url: a public
 * https URL the backend uploads to the ad account. A picture ad stays a
 * picture ad and a video ad a video ad (409 FORMAT_MISMATCH otherwise). The
 * goal is not sent: Meta doesn't change a running campaign's objective.
 */
import { fetchApi } from "../../components/marketing/access";
import { metaHeaders } from "../../lib/ads";
import { CTA_META, buildTargeting, optimisationSettings } from "./campaignModel";

/** What Edit can reach on a campaign read from GET /api/ads/campaigns/:id. */
export function editTarget(row, seedId) {
  const set = row.adset || null;
  const creative = row.creative || null;
  const spec = creative?.object_story_spec || {};
  return {
    id: row.id || seedId,
    name: row.name || "",
    live: row.live === true,
    adsetId: set?.id || null,
    adsetCount: (row.adsets || []).length,
    adId: creative?.ad_id || null,
    adCount: (row.ads || []).length,
    // A daily budget on the ad set is what the canvas edits; a lifetime one, or a budget on the campaign, is not.
    budgetKind: !set ? null : set.daily_budget ? "daily" : set.lifetime_budget ? "lifetime" : "campaign",
    started: set?.start_time ? new Date(set.start_time).getTime() <= Date.now() : false,
    startedAt: set?.start_time || null,
    targeting: set?.targeting || null,
    // The backend edits single image and video ads built from a story spec (not a boosted post).
    creativeEditable: !!(creative?.ad_id && spec.page_id && (spec.link_data || spec.video_data)),
    video: !!spec.video_data,
  };
}

// The draft fields each part owns.
const PART_FIELDS = {
  name: ["name"],
  adset: ["budget", "start", "startDate", "endDate", "locations", "regions", "countries", "ageMin", "ageMax", "gender", "languages", "interests", "customAudiences", "excludedAudiences", "placement"],
  creative: ["headline", "primary", "description", "url", "cta", "images", "variant", "videoUrl"],
  olli: ["olli"],
};
const PART_LABEL = { name: "Name", adset: "Budget, schedule and audience", creative: "Words, link and button", olli: "Olli optimising" };

// Targeting keys the canvas owns; anything else on the ad set (automation, devices…) is kept as it is.
const MANAGED = ["age_min", "age_max", "excluded_geo_locations", "genders", "locales", "custom_audiences", "excluded_custom_audiences", "publisher_platforms", "facebook_positions", "instagram_positions", "messenger_positions", "audience_network_positions"];
const GEO_MANAGED = ["cities", "regions", "custom_locations", "countries"];

/** The ad set's targeting with the canvas's places and people laid over it. */
export function mergeTargeting(original, next) {
  const o = original || {};
  const out = { ...o };
  MANAGED.forEach((k) => delete out[k]);
  delete out.flexible_spec;
  const { geo_locations: nextGeo, flexible_spec: nextFlex, ...rest } = next;
  Object.assign(out, rest);
  // Zip codes, places and the like stay; with any of them a country is not added on top.
  const extra = Object.fromEntries(Object.entries(o.geo_locations || {}).filter(([k]) => !GEO_MANAGED.includes(k)));
  const geo = { ...extra, ...nextGeo };
  const keptPlaces = Object.keys(extra).some((k) => k !== "location_types");
  if (keptPlaces && !(o.geo_locations?.countries || []).length && !nextGeo.cities && !nextGeo.custom_locations && !nextGeo.regions) delete geo.countries;
  out.geo_locations = geo;
  // Behaviours and other narrowing in flexible_spec stay; interests are the canvas's.
  const others = (o.flexible_spec || []).map(({ interests, ...r }) => r).filter((r) => Object.keys(r).length);
  const flex = [...(nextFlex || []), ...others];
  if (flex.length) out.flexible_spec = flex;
  return out;
}

/** The picture a draft runs: the chosen one of its pictures. */
export const pictureOf = (d) => d.images?.[d.variant]?.url || "";
// Meta fetches the new picture or video itself, so only a public https address will do (never a blob: preview).
const fetchable = (u) => /^https:\/\/\S+$/i.test(u || "");

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const startIso = (d) => (d.start === "later" && d.startDate ? new Date(`${d.startDate}T09:00:00`).toISOString() : new Date(Date.now() + 10 * 60_000).toISOString());
const endIso = (d) => (d.endDate ? new Date(`${d.endDate}T23:59:00`).toISOString() : null);

/**
 * What would be sent: [{ part, label, request, blocked }] for every part that
 * differs from the base. `blocked` says why a changed part can't go to Meta.
 */
export function pendingParts(target, base, draft) {
  const out = [];
  if (draft.name.trim() !== base.name.trim()) {
    out.push({ part: "name", request: { method: "patch", path: `/api/ads/campaigns/${encodeURIComponent(target.id)}`, body: { name: draft.name.trim() } } });
  }
  const set = {};
  let setBlocked = "";
  if (Number(draft.budget) !== Number(base.budget)) {
    if (target.budgetKind === "daily") set.daily_budget = Number(draft.budget);
    else setBlocked = target.budgetKind === "lifetime" ? "This campaign has a lifetime budget; change it in Meta Ads Manager." : "This campaign's budget is set on the campaign; change it in Meta Ads Manager.";
  }
  if (!target.started && (draft.start !== base.start || draft.startDate !== base.startDate)) set.start_time = startIso(draft);
  if (draft.endDate !== base.endDate) set.end_time = endIso(draft);
  const nextTargeting = buildTargeting(draft);
  if (!same(nextTargeting, buildTargeting(base))) set.targeting = mergeTargeting(target.targeting, nextTargeting);
  if (Object.keys(set).length || setBlocked) {
    out.push({
      part: "adset",
      request: Object.keys(set).length && target.adsetId ? { method: "patch", path: `/api/ads/adsets/${encodeURIComponent(target.adsetId)}`, body: set } : null,
      blocked: !target.adsetId ? "Olli couldn't read this campaign's ad set from Meta. Connect Meta again in Settings → Integrations, then reopen Edit." : setBlocked,
    });
  }
  const words = {};
  if (draft.headline !== base.headline) words.headline = draft.headline;
  if (draft.primary !== base.primary) words.primary_text = draft.primary;
  if (draft.description !== base.description) words.description = draft.description;
  if (draft.url !== base.url) words.link = draft.url.trim();
  if (draft.cta !== base.cta && CTA_META[draft.cta]) words.call_to_action = CTA_META[draft.cta];
  // A new picture (picture ads) or video (video ads), only when it differs from what runs now.
  const kind = target.video ? "video" : "picture";
  const media = target.video ? (draft.videoUrl && draft.videoUrl !== base.videoUrl ? draft.videoUrl : "") : (pictureOf(draft) && pictureOf(draft) !== pictureOf(base) ? pictureOf(draft) : "");
  if (media) words[target.video ? "video_url" : "image_url"] = media;
  if (Object.keys(words).length) {
    const onlyMedia = Object.keys(words).length === 1 && media;
    out.push({
      part: "creative",
      label: onlyMedia ? `New ${kind}` : media ? `Words and ${kind}` : PART_LABEL.creative,
      request: target.creativeEditable ? { method: "patch", path: `/api/ads/ads/${encodeURIComponent(target.adId)}/creative`, body: words } : null,
      blocked: !target.creativeEditable
        ? (!target.adId ? "Olli couldn't read this campaign's ad from Meta." : "This ad can't be changed from Olli (it runs an existing post, or a format Olli doesn't edit). Change it in Meta Ads Manager.")
        : media && !fetchable(media) ? `The new ${kind} isn't at a public https address Meta can fetch. Upload it again or choose another.` : "",
    });
  }
  const olli = optimisationSettings(draft.olli);
  if (!same(olli, optimisationSettings(base.olli))) {
    out.push({ part: "olli", request: { method: "put", path: `/api/ads/campaigns/${encodeURIComponent(target.id)}/ai-optimization`, body: olli } });
  }
  return out.map((p) => ({ ...p, label: p.label || PART_LABEL[p.part], blocked: p.blocked || "" }));
}

const reasonText = (r) => {
  if (r.data?.requires_user_token || r.data?.requires_reconnect) return "Connect Meta again in Settings → Integrations, then try again.";
  if (r.code === "PERMISSION_DENIED") return r.message || "You can view this campaign but not change it.";
  // A new video Meta hasn't finished processing within the backend's two minutes (502).
  if (r.code === "VIDEO_NOT_READY") return "Meta is still processing the video, try again in a minute.";
  // FORMAT_MISMATCH and CREATIVE_NOT_EDITABLE (409) carry the backend's own explanation.
  return r.message || (r.status ? "Meta didn't take this change. Try again." : "Can't reach Olli right now. Check your connection.");
};

/**
 * Send the pending parts one after another. Resolves
 * [{ part, label, ok, message }] in the same order; a blocked part is not sent.
 */
export async function saveParts(parts) {
  const results = [];
  for (const p of parts) {
    if (p.blocked || !p.request) { results.push({ part: p.part, label: p.label, ok: false, message: p.blocked || "Nothing to send." }); continue; }
    const r = await fetchApi(p.request.path, { method: p.request.method, body: p.request.body, headers: metaHeaders().headers });
    results.push({
      part: p.part, label: p.label, ok: r.ok,
      message: r.ok ? (p.part === "creative" ? r.data?.data?.note || "The ad goes back to Meta review before the new version shows." : "") : reasonText(r),
    });
  }
  return results;
}

/** The base with the saved parts' fields taken from the draft, so a retry sends only what failed. */
export function withSaved(base, draft, results) {
  const next = { ...base };
  results.filter((r) => r.ok).forEach((r) => PART_FIELDS[r.part].forEach((k) => { next[k] = draft[k]; }));
  return next;
}

/*
 * Meta's ad-language names for targeting.locales keys, from
 * GET /api/ads/locales?keys=6,46 → [{ key, name }]. One call per set of keys
 * not seen yet; each key keeps its promise, so a name is asked once per visit.
 * A failed call resolves null and the screen keeps "Language <n>".
 */
const localeCache = new Map(); // key → Promise<name | null>
export function languageNames(keys) {
  const missing = [...new Set(keys.map(String))].filter((k) => !localeCache.has(k));
  if (missing.length) {
    const call = fetchApi("/api/ads/locales", { params: { keys: missing.join(",") }, headers: metaHeaders().headers })
      .then((r) => new Map((r.ok && Array.isArray(r.data?.data) ? r.data.data : []).map((l) => [String(l.key), l.name || null])));
    missing.forEach((k) => localeCache.set(k, call.then((m) => m.get(k) || null)));
  }
  return Promise.all(keys.map((k) => localeCache.get(String(k)))).then((names) => new Map(keys.map((k, i) => [String(k), names[i]]).filter(([, n]) => n)));
}
/** The languages with Meta's names in place of "Language <n>" where one is known. */
export const named = (list, names) => (list || []).map((l) => (l.name === `Language ${l.key}` && names.get(String(l.key)) ? { ...l, name: names.get(String(l.key)) } : l));
